import React, { useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { capitalize } from "@mui/material";
import { IChoice, RootStateType } from "@Interfaces";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PhotoIcon from "@mui/icons-material/Photo";

import { TendbeQRCode } from "@Components/TendbeQRCode/TendbeQRCode";
import GetImagePath from "@Hooks/getImagePath";
import {
    ChoicesText,
    Question,
    HighlightText,
    GradientBackground,
} from "./ExportToDialog.styles";
import { PDFDocument } from "pdf-lib";
import html2canvas from "html2canvas";
import { useTranslation, Trans } from "react-i18next";
import { NotificationActions } from "@Store";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const EXPORT_CANVAS_WIDTH = 816;
const EXPORT_CANVAS_HEIGHT = 1056;

type QRLayoutColumns = 1 | 2 | 3 | 4;

type QrLayoutConfig = {
    columns: QRLayoutColumns;
    qrSize: number;
    logoSize: number;
    gridSpacing: number;
    cardPadding: number;
    cardMargin: number;
    choiceTextMargin: string;
    choiceFontSize: string;
};

type SharedQrProps = {
    openDialog: boolean;
    link: string;
    channel_title: string;
    channel_description: string;
    question: string;
    choices: IChoice[];
    pollId: string;
    channel_id: string;
};

type ChoiceTextParts = {
    primary: string;
    secondary: string;
};

const BASE_LOGO_RATIO = 0.26;

// --- Random Gradient Helpers ---
const generateRandomColor = (): string => {
    const randomColor = Math.floor(Math.random() * 16777215).toString(16);
    return `#${randomColor.padStart(6, "0")}`;
};

const getRandomGradient = () => ({
    startColor: generateRandomColor(),
    endColor: generateRandomColor(),
});

const splitChoiceText = (txt?: string): ChoiceTextParts => {
    const value = (txt || "").trim();
    if (!value) {
        return { primary: "", secondary: "" };
    }

    const separatorIndex = value.indexOf(":");
    if (separatorIndex === -1) {
        return { primary: value, secondary: "" };
    }

    return {
        primary: value.slice(0, separatorIndex).trim(),
        secondary: value.slice(separatorIndex + 1).trim(),
    };
};

const isNpsLikeChoices = (choices: IChoice[]) => {
    if (!choices || choices.length < 10) {
        return false;
    }

    return choices.every((choice, index) => {
        const { primary } = splitChoiceText(choice?.txt);
        const numericMatch = primary.match(/\d+/);
        if (!numericMatch) {
            return false;
        }
        return Number(numericMatch[0]) === index;
    });
};

const getExportQrLayoutConfig = (itemCount: number): QrLayoutConfig => {
    const normalizedCount = Math.max(itemCount, 1);
    let columns: QRLayoutColumns;

    if (normalizedCount === 1) {
        columns = 1;
    } else if (normalizedCount <= 3) {
        columns = normalizedCount as QRLayoutColumns;
    } else if (normalizedCount === 4) {
        columns = 2;
    } else if (normalizedCount <= 8) {
        columns = 3;
    } else {
        columns = 4;
    }

    const sizeByColumns: Record<QRLayoutColumns, number> = {
        1: 336,
        2: normalizedCount === 4 ? 210 : 260,
        3: normalizedCount <= 6 ? 170 : 150,
        4: 128,
    };

    const qrSize = sizeByColumns[columns];
    const logoSize = Math.round(qrSize * BASE_LOGO_RATIO);
    const isDenseLayout = normalizedCount >= 7;

    return {
        columns,
        qrSize,
        logoSize,
        gridSpacing: isDenseLayout ? 1 : 2,
        cardPadding: isDenseLayout ? 1 : 2,
        cardMargin: isDenseLayout ? 0.5 : 1,
        choiceTextMargin: isDenseLayout ? "8px 0 0" : "25px",
        choiceFontSize: isDenseLayout ? "0.8rem" : "1rem",
    };
};

const getPreviewQrLayoutConfig = (
    itemCount: number,
    isMobile: boolean
): QrLayoutConfig => {
    const normalizedCount = Math.max(itemCount, 1);
    const columns: QRLayoutColumns =
        normalizedCount === 1
            ? 1
            : isMobile && normalizedCount === 2
            ? 1
            : isMobile
            ? 2
            : normalizedCount <= 4
            ? 2
            : 3;

    const sizeByColumnsMobile: Record<QRLayoutColumns, number> = {
        1: 220,
        2: 112,
        3: 104,
        4: 96,
    };

    const sizeByColumnsDesktop: Record<QRLayoutColumns, number> = {
        1: 260,
        2: 140,
        3: 120,
        4: 108,
    };

    const qrSize = isMobile
        ? sizeByColumnsMobile[columns]
        : sizeByColumnsDesktop[columns];
    const logoSize = Math.round(qrSize * BASE_LOGO_RATIO);
    const isTwoOptionMobile = isMobile && normalizedCount === 2;

    return {
        columns,
        qrSize,
        logoSize,
        gridSpacing: isTwoOptionMobile ? 1 : 2,
        cardPadding: isTwoOptionMobile ? 1 : 0.5,
        cardMargin: 0.25,
        choiceTextMargin: isTwoOptionMobile ? "8px 0 0" : "6px 0 0",
        choiceFontSize: isTwoOptionMobile
            ? "0.84rem"
            : isMobile
            ? "0.72rem"
            : "0.78rem",
    };
};

const ExportToDialog: React.FC<Partial<SharedQrProps>> = ({
    link,
    channel_title,
    channel_description,
    question,
    choices,
    pollId,
    channel_id,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const previewContainerRef = useRef<HTMLDivElement>(null);
    const exportTemplateRef = useRef<HTMLDivElement>(null);

    const channel = useSelector((state: RootStateType) =>
        state.channels.channels.find((ch) => ch._id === channel_id)
    );

    const isProfilePictureVisible =
        channel?.settings?.display_profile_picture ?? true;

    const profileImgPath = GetImagePath(
        isProfilePictureVisible
            ? channel?.profile_image?.public_url
            : undefined,
        isProfilePictureVisible ? channel?.profile_image?.fid : undefined
    );

    const useChannelProfileAsQrLogo =
        isProfilePictureVisible &&
        (channel?.settings?.use_channel_profile_as_qr_logo ?? false);
    const qrCodeColor = channel?.settings?.qr_code_color;

    const generateVotingLink = useCallback(
        (choiceId: string) => {
            return `${window.location.protocol}//${window.location.host}/single?pid=${pollId}&cid=${choiceId}`;
        },
        [pollId]
    );

    const sanitizeFileName = (name: string) => {
        return name.replace(/[^a-z0-9]/gi, "_").toLowerCase();
    };

    const headerText = useMemo(
        () => capitalize(question || channel_title || ""),
        [question, channel_title]
    );

    const gradientColors = useMemo(() => getRandomGradient(), []);

    const choiceCount = choices?.length ?? 0;
    const hasChoices = choiceCount > 0;
    const isNpsLike = useMemo(() => isNpsLikeChoices(choices || []), [choices]);

    const npsHints = useMemo(() => {
        if (!isNpsLike || !choices || choices.length === 0) {
            return { minHint: "", maxHint: "" };
        }

        const first = splitChoiceText(choices[0]?.txt);
        const last = splitChoiceText(choices[choices.length - 1]?.txt);

        return {
            minHint: first.secondary,
            maxHint: last.secondary,
        };
    }, [choices, isNpsLike]);

    const previewLayout = useMemo(
        () => getPreviewQrLayoutConfig(choiceCount, isMobile),
        [choiceCount, isMobile]
    );

    const exportLayout = useMemo(
        () => getExportQrLayoutConfig(choiceCount),
        [choiceCount]
    );

    const qrPixelRatio = 4;

    const notifyExportError = useCallback(() => {
        dispatch(
            NotificationActions.notification({
                snackbarOpen: true,
                snackbarType: "error",
                snackbarMessage: t("snackbar_fail"),
            })
        );
    }, [dispatch, t]);

    const getChoiceLabel = useCallback(
        (txt: string, index: number, compact: boolean) => {
            const letter = String.fromCharCode(65 + index);
            const { primary } = splitChoiceText(txt);

            if (compact || isNpsLike) {
                return `${letter}) ${primary || txt}`;
            }

            return `${letter}) ${txt}`;
        },
        [isNpsLike]
    );

    const cloneCanvases = (original: HTMLElement, clone: HTMLElement) => {
        const originalCanvases = original.querySelectorAll("canvas");
        const clonedCanvases = clone.querySelectorAll("canvas");

        originalCanvases.forEach((origCanvas, index) => {
            const dataURL = origCanvas.toDataURL("image/png");
            const img = document.createElement("img");
            img.src = dataURL;
            img.width = origCanvas.width;
            img.height = origCanvas.height;

            const computedStyle = window.getComputedStyle(origCanvas);
            img.style.width = computedStyle.width;
            img.style.height = computedStyle.height;
            img.style.borderRadius = computedStyle.borderRadius;
            img.style.border = computedStyle.border;
            img.style.display = computedStyle.display;
            img.style.margin = computedStyle.margin;
            img.style.maxWidth = computedStyle.maxWidth;
            img.style.maxHeight = computedStyle.maxHeight;
            img.style.boxSizing = computedStyle.boxSizing;
            img.style.padding = computedStyle.padding;

            const clonedCanvas = clonedCanvases[index];
            if (clonedCanvas && clonedCanvas.parentNode) {
                clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
            }
        });
    };

    const captureExport = useCallback(
        async (fileType: "pdf" | "jpg") => {
            if (!exportTemplateRef.current) {
                return;
            }

            let clone: HTMLElement | null = null;

            try {
                clone = exportTemplateRef.current.cloneNode(
                    true
                ) as HTMLElement;
                clone.style.position = "absolute";
                clone.style.top = "-9999px";
                clone.style.left = "0";
                clone.style.opacity = "1";
                clone.style.pointerEvents = "none";
                document.body.appendChild(clone);

                cloneCanvases(exportTemplateRef.current, clone);

                const canvas = await html2canvas(clone, {
                    backgroundColor: "#ffffff",
                    scale: 3,
                    useCORS: true,
                });

                const fileName = sanitizeFileName(headerText) || "export";

                if (fileType === "pdf") {
                    const imgData = canvas.toDataURL("image/png");
                    const pdf = await PDFDocument.create();
                    const pageWidth = 612;
                    const pageHeight = 792;
                    const page = pdf.addPage([pageWidth, pageHeight]);
                    const pngImage = await pdf.embedPng(imgData);

                    const scale = Math.min(
                        pageWidth / pngImage.width,
                        pageHeight / pngImage.height
                    );

                    const renderedWidth = pngImage.width * scale;
                    const renderedHeight = pngImage.height * scale;

                    page.drawImage(pngImage, {
                        x: Math.max((pageWidth - renderedWidth) / 2, 0),
                        y: Math.max((pageHeight - renderedHeight) / 2, 0),
                        width: renderedWidth,
                        height: renderedHeight,
                    });

                    const pdfBytes = await pdf.save();
                    const blob = new Blob([pdfBytes], {
                        type: "application/pdf",
                    });
                    const pdfUrl = URL.createObjectURL(blob);
                    const linkEl = document.createElement("a");
                    linkEl.download = `${fileName}.pdf`;
                    linkEl.href = pdfUrl;
                    linkEl.click();
                    URL.revokeObjectURL(pdfUrl);
                    return;
                }

                const imgData = canvas.toDataURL("image/jpeg", 1.0);
                const linkEl = document.createElement("a");
                linkEl.download = `${fileName}.jpg`;
                linkEl.href = imgData;
                linkEl.click();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Failed to export.", error);
                notifyExportError();
            } finally {
                if (clone?.parentNode) {
                    clone.parentNode.removeChild(clone);
                }
            }
        },
        [headerText, notifyExportError]
    );

    const renderChoicesGrid = useCallback(
        (layout: QrLayoutConfig, mode: "preview" | "export") => {
            if (!choices || choiceCount === 0) {
                return null;
            }

            const {
                columns,
                qrSize,
                logoSize,
                gridSpacing,
                cardPadding,
                cardMargin,
                choiceTextMargin,
                choiceFontSize,
            } = layout;

            const xsSize = (12 / columns) as 12 | 6 | 4 | 3;
            const isCompact = mode === "preview" || choiceCount >= 7;

            return (
                <Grid
                    container
                    spacing={gridSpacing}
                    direction="row"
                    justifyContent="center"
                >
                    {choices.map(({ txt, _id }, index) => (
                        <Grid item key={_id || index} xs={xsSize}>
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: "transparent",
                                    p: cardPadding,
                                    textAlign: "center",
                                    color: "text.secondary",
                                    m: cardMargin,
                                }}
                                id={
                                    hasChoices
                                        ? generateVotingLink(_id)
                                        : undefined
                                }
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        width: "100%",
                                    }}
                                >
                                    <TendbeQRCode
                                        value={generateVotingLink(_id)}
                                        profileImgPath={profileImgPath}
                                        qrColor={qrCodeColor}
                                        qrCodeHeight={logoSize}
                                        qrCodeWidth={logoSize}
                                        qrSize={qrSize}
                                        pixelRatio={qrPixelRatio}
                                        useChannelProfileAsQrLogo={
                                            useChannelProfileAsQrLogo
                                        }
                                    />
                                </div>
                                <ChoicesText
                                    style={{
                                        margin: choiceTextMargin,
                                        fontSize: choiceFontSize,
                                    }}
                                >
                                    {getChoiceLabel(txt, index, isCompact)}
                                </ChoicesText>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            );
        },
        [
            qrCodeColor,
            choiceCount,
            choices,
            generateVotingLink,
            getChoiceLabel,
            hasChoices,
            profileImgPath,
            useChannelProfileAsQrLogo,
        ]
    );

    const renderChannelQr = useCallback(
        (layout: QrLayoutConfig, mode: "preview" | "export") => {
            const descriptionMaxWidth = mode === "preview" ? "18rem" : "24rem";
            return (
                <Grid container direction="column" alignItems="center">
                    <Grid item id={link}>
                        <TendbeQRCode
                            value={link as string}
                            profileImgPath={profileImgPath}
                            qrColor={qrCodeColor}
                            qrCodeHeight={layout.logoSize}
                            qrCodeWidth={layout.logoSize}
                            qrSize={layout.qrSize}
                            pixelRatio={qrPixelRatio}
                            useChannelProfileAsQrLogo={
                                useChannelProfileAsQrLogo
                            }
                        />
                    </Grid>
                    <Grid item>
                        <p
                            style={{
                                maxWidth: descriptionMaxWidth,
                                marginTop: "1rem",
                                textAlign: "center",
                            }}
                        >
                            {channel_description}
                        </p>
                    </Grid>
                </Grid>
            );
        },
        [
            qrCodeColor,
            channel_description,
            link,
            profileImgPath,
            useChannelProfileAsQrLogo,
        ]
    );

    const npsScaleHints = isNpsLike ? (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                width: "100%",
                px: 1,
                mt: 1,
                mb: 1,
                fontSize: "0.75rem",
                color: "text.secondary",
                fontWeight: 600,
            }}
        >
            <span>{npsHints.minHint}</span>
            <span>{npsHints.maxHint}</span>
        </Box>
    ) : null;

    return (
        <>
            <div
                ref={previewContainerRef}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    minHeight: "68vh",
                    gap: "12px",
                    paddingBottom: "84px",
                }}
            >
                <GradientBackground
                    startColor={gradientColors.startColor}
                    endColor={gradientColors.endColor}
                    style={{
                        minHeight: isMobile ? "160px" : "200px",
                        marginBottom: "8px",
                    }}
                >
                    <HighlightText>
                        <Question
                            style={
                                choiceCount > 8
                                    ? { fontSize: "22px" }
                                    : choiceCount > 4
                                    ? { fontSize: "26px" }
                                    : undefined
                            }
                        >
                            {headerText}
                        </Question>
                    </HighlightText>
                </GradientBackground>

                {hasChoices ? (
                    <>
                        {npsScaleHints}
                        <div
                            style={{
                                width: "100%",
                                padding: isMobile ? "0 4px" : "0 8px",
                            }}
                        >
                            {renderChoicesGrid(previewLayout, "preview")}
                        </div>
                    </>
                ) : (
                    renderChannelQr(previewLayout, "preview")
                )}
            </div>

            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    borderTop: "1px solid #e5e7eb",
                    pt: 1,
                    pb: 0.5,
                    zIndex: 3,
                }}
            >
                <Grid container direction="row" justifyContent="space-evenly">
                    <Grid
                        item
                        style={{ margin: "0.5rem 0.75rem" }}
                        className="cursor-pointer"
                        onClick={() => captureExport("pdf")}
                    >
                        <Grid container direction="column" alignItems="center">
                            <Grid item>
                                <PictureAsPdfIcon className="fs-40" />
                            </Grid>
                            <Grid item>{t("dialogs.export.as_pdf")}</Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        style={{ margin: "0.5rem 0.75rem" }}
                        className="cursor-pointer"
                        onClick={() => captureExport("jpg")}
                    >
                        <Grid container direction="column" alignItems="center">
                            <Grid item>
                                <PhotoIcon className="fs-40" />
                            </Grid>
                            <Grid item>{t("dialogs.export.as_image")}</Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <div
                ref={exportTemplateRef}
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: 0,
                    left: "-10000px",
                    width: `${EXPORT_CANVAS_WIDTH}px`,
                    height: `${EXPORT_CANVAS_HEIGHT}px`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    padding: "8px",
                    boxSizing: "border-box",
                    backgroundColor: "#fff",
                    opacity: 0,
                    pointerEvents: "none",
                }}
            >
                <GradientBackground
                    startColor={gradientColors.startColor}
                    endColor={gradientColors.endColor}
                    style={{ minHeight: "200px", marginBottom: "16px" }}
                >
                    <HighlightText>
                        <Question
                            style={
                                choiceCount > 8
                                    ? { fontSize: "24px" }
                                    : choiceCount > 4
                                    ? { fontSize: "28px" }
                                    : undefined
                            }
                        >
                            {headerText}
                        </Question>
                    </HighlightText>
                </GradientBackground>

                <div
                    style={{
                        flex: "1 0 auto",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "stretch",
                    }}
                >
                    {hasChoices ? (
                        <>
                            {npsScaleHints}
                            <div style={{ width: "100%" }}>
                                {renderChoicesGrid(exportLayout, "export")}
                            </div>
                        </>
                    ) : (
                        renderChannelQr(exportLayout, "export")
                    )}
                </div>

                <div
                    data-export-footer="true"
                    style={{
                        display: "block",
                        fontSize: "10px",
                        color: "#000",
                        textAlign: "center",
                        width: "100%",
                        marginTop: "auto",
                        padding: "8px",
                    }}
                >
                    <p>
                        <Trans
                            i18nKey="dialogs.export.privacy_notice"
                            components={{
                                link: (
                                    <a href="https://tendbe.com/privacy">
                                        https://tendbe.com/privacy
                                    </a>
                                ),
                            }}
                        />
                    </p>
                </div>
            </div>
        </>
    );
};

export default {
    content: ExportToDialog,
    title: "Export QR",
    canceled: true,
};
